import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, {type Config} from 'datatables.net';
import type {CreateInstanceProps, Flavor} from "../../lib/ModelTypes.ts";
import {datatable_language_pack} from "./DatatableLanguagePack.tsx";
import {useCallback, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";


type DataTableProps = {
    items?: Flavor[];
    // setData: Dispatch<SetStateAction<CreateInstanceProps>>;
    onChangeFlavor?: (flavor: Flavor, e: Event,) => void;
    data: CreateInstanceProps;
    radioGroupName?: string;
};

const FlavorVmTable = ({items = [], onChangeFlavor, data, radioGroupName = "flavorSelect"}: DataTableProps) => {
    const {t} = useTranslation();

    DataTable.use(DT);

    const dataTableRef = useRef<DataTableRef>(null);
    const changeFlavor = useCallback((newFlavor: Flavor, e: Event,) => {
        if (newFlavor.id !== data.selectedFlavor && onChangeFlavor) {
            onChangeFlavor(newFlavor, e);
        }
    }, [data.selectedFlavor, onChangeFlavor]);

    const columns = [
        {
            data: 'name',
            width: '6%',
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            render: (_data: string, _type: string, row: Flavor) => {
                return `<input type="radio" name="${radioGroupName}" class="flavor-radio form-check-input" value="${row.id}" ${data.selectedFlavor === row.id ? 'checked' : ''} />`
            },
        },
        { title: t("instance.instanceName"), data: 'name', width: '20%' },
        {
            title: t("instance.memory_GB"), data: 'ram', width: '22%', render: (data: number) => `${data / 1024}GB`
        },
        {
            title: t("instance.storage_GB"), data: 'disk', width: '12%', render: (data: number) => `${data}GB`
        },
        {
            title: t("instance.numberInUse"), data: 'usage', width: '9%', render: (data: number) => t('instance.instancesNoFmt', {n: data})
        },
        {
            title: t("instance.numberRemaining"), data: 'id', width: '9%', render: (data: string, _type: string, row: Flavor) => {
                if (row.id === data) {
                    return t('instance.instancesNoFmt', {n: row.quota - row.usage});
                }
                return '';
            }
        },
        {
            title: t("instance.totalAmount"), data: 'quota', width: '9%', render: (data: number) => t('instance.instancesNoFmt', {n: data})
        },
    ];
    const options: Config = {
        destroy: true,
        paging: false,
        searching: false,
        order: [[1, "asc"]],
        ordering: false,
        lengthChange: false,
        info: false,
        autoWidth: false,
        language: datatable_language_pack,
        columnDefs: [
            { targets: "_all", className: "dt-center" },
        ],
    }

    useEffect(() => {
        // 화면이 다시 그려질때 dom을 바인딩하여야 react가 정상 동작
        // option 의 createdRow를 사용하면 안됨
        const tableNodeApi = dataTableRef.current?.dt();
        if (!tableNodeApi) return;

        const count = tableNodeApi.rows().count();
        const rowsData = tableNodeApi.data().toArray() as Flavor[];

        const eventSets: {
            flavorSelectRadio: HTMLInputElement | null;
            radioClickCallback: (e: Event) => void;
            rowNode: HTMLTableRowElement | null;
            rowClickCallback: (e: Event) => void;
        }[] = [];

        for (let i = 0; i < count; i++) {
            const cell = tableNodeApi.cell(i, 0).node() as HTMLElement;
            const rowNode = tableNodeApi.row(i).node() as HTMLTableRowElement | null;
            const flavorSelectRadio = cell.querySelector(`input[name="${radioGroupName}"]`) as HTMLInputElement | null;

            if (!flavorSelectRadio || !rowNode) continue;

            // 라디오 직접 클릭
            const radioClickCallback = (e: Event) => {
                e.stopPropagation();
                changeFlavor(rowsData[i], e);
            };

            // 행 클릭
            const rowClickCallback = (e: Event) => {
                const target = e.target as HTMLElement | null;
                if (target?.closest('input, button, a, label')) return;
                changeFlavor(rowsData[i], e);
            };

            flavorSelectRadio.addEventListener("click", radioClickCallback);
            rowNode.addEventListener("click", rowClickCallback);
            rowNode.style.cursor = "pointer";

            eventSets.push({
                flavorSelectRadio,
                radioClickCallback,
                rowNode,
                rowClickCallback,
            });
        }

        return () => {
            eventSets.forEach(({ flavorSelectRadio, radioClickCallback, rowNode, rowClickCallback }) => {
                flavorSelectRadio?.removeEventListener("click", radioClickCallback);
                rowNode?.removeEventListener("click", rowClickCallback);
            });
        };
    }, [changeFlavor, radioGroupName, t]);


    return (
        <div style={{fontSize: "14px"}} className="ms-3 me-4">
            <DataTable
                key={`vm-${data.selectedFlavor ?? "none"}`}
                data={items}
                columns={columns}
                className="display"
                options={options}
                ref={dataTableRef}>
                <thead style={{backgroundColor: "#E2E3E5"}}></thead>
            </DataTable>
        </div>
    );
};

export default FlavorVmTable;